import { mdiClose } from '@mdi/js'
import Icon from '@mdi/react'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { OVERLAY_SIZES, OverlaySize } from 'components/overlaySizes'
import styles from './PPDialog.pcss'

const DEFAULT_SIZE: OverlaySize = 'lg'

export interface PPDialogProps {
  open: boolean
  title: React.ReactNode
  onClose?: () => void
  footer?: React.ReactNode
  size?: OverlaySize
  // Defaults to true. When false the dialog blocks: no close affordance, and Esc/backdrop won't dismiss it.
  dismissible?: boolean
  // Override the modal's stacking order — needed when it must sit above other modals/snackbars (e.g. a blocking app gate).
  zIndex?: number
  // Keep the children mounted while closed, so reopening does not remount them (and refetch their data).
  keepMounted?: boolean
  className?: string
  children: React.ReactNode
  'data-testid'?: string
}

export function PPDialog(props: PPDialogProps) {
  const intl = useIntl()
  const titleId = React.useId()
  const width = OVERLAY_SIZES[props.size ?? DEFAULT_SIZE]
  const dismissible = props.dismissible ?? true
  return (
    <Dialog
      open={props.open}
      onClose={dismissible ? props.onClose : undefined}
      disableEscapeKeyDown={!dismissible}
      keepMounted={props.keepMounted}
      maxWidth={false}
      aria-labelledby={titleId}
      data-testid={props['data-testid']}
      classes={{ paper: `${styles.paper} ${props.className || ''}` }}
      PaperProps={{ style: { width } }}
      sx={props.zIndex !== undefined ? { zIndex: props.zIndex } : undefined}
    >
      <div className={styles.root}>
        <header className={styles.header}>
          <h2 id={titleId} className={styles.title}>{props.title}</h2>
          {dismissible && (
            <IconButton
              className={styles['btn-close']}
              aria-label={intl.formatMessage({ id: 'actions.close' })}
              onClick={props.onClose}
            >
              <Icon path={mdiClose} size="20px" />
            </IconButton>
          )}
        </header>
        <div className={styles.body}>{props.children}</div>
        {props.footer ? <footer className={styles.footer}>{props.footer}</footer> : null}
      </div>
    </Dialog>
  )
}

export default PPDialog
